import { useState } from 'react';
import {
  Stack,
  Paper,
  Group,
  Text,
  Textarea,
  NumberInput,
  Button,
  Table,
  Skeleton,
  Progress,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSparkles,
  IconShoppingCartPlus,
  IconAlertTriangle,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useRecipeToOrder, useAddToCart } from '@/hooks/api';

export default function RecipeToCart() {
  const [recipeText, setRecipeText] = useState('');
  const [servings, setServings] = useState<number>(4);
  const [addingToCart, setAddingToCart] = useState(false);

  const recipeToOrder = useRecipeToOrder();
  const addToCart = useAddToCart();

  const result = recipeToOrder.data?.data;

  const handleConvert = () => {
    if (!recipeText.trim()) {
      notifications.show({
        title: 'Missing Recipe',
        message: 'Please paste or type a recipe to convert',
        color: 'orange',
      });
      return;
    }
    recipeToOrder.mutate({ recipe_text: recipeText, servings });
  };

  const handleAddItem = async (product: {
    product_id: number;
    product_name: string;
    quantity: number;
  }) => {
    try {
      await addToCart.mutateAsync({
        product_id: product.product_id,
        quantity: product.quantity,
      });
      notifications.show({
        title: 'Added to Cart',
        message: `${product.product_name} added to cart`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add item to cart',
        color: 'red',
      });
    }
  };

  const handleAddAllToCart = async () => {
    if (!result?.products?.length) return;
    setAddingToCart(true);
    let successCount = 0;
    try {
      for (const item of result.products) {
        if (item.quantity > 0) {
          await addToCart.mutateAsync({
            product_id: item.product_id,
            quantity: item.quantity,
          });
          successCount++;
        }
      }
      if (successCount > 0) {
        notifications.show({
          title: 'Success',
          message: `Added ${successCount} item${successCount > 1 ? 's' : ''} to cart`,
          color: 'green',
        });
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add items to cart',
        color: 'red',
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const confidenceColor = (c: number) =>
    c >= 0.8 ? 'green' : c >= 0.5 ? 'yellow' : 'red';

  return (
    <Stack gap="lg">
      <PageHeader
        title="Recipe to Cart"
        description="Paste a recipe and let AI find the products you need"
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--mantine-spacing-lg)' }}>
        {/* Left: Input */}
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Your Recipe</Text>
            <Textarea
              placeholder="Paste your recipe here... e.g.&#10;&#10;Grilled Caesar Salad&#10;2 heads romaine lettuce&#10;1/2 cup parmesan cheese, shaved&#10;1 cup croutons&#10;4 tbsp caesar dressing&#10;2 chicken breasts, grilled"
              minRows={12}
              autosize
              value={recipeText}
              onChange={(e) => setRecipeText(e.currentTarget.value)}
            />
            <NumberInput
              label="Servings"
              value={servings}
              onChange={(v) => setServings(Number(v) || 1)}
              min={1}
              max={1000}
              style={{ maxWidth: 160 }}
            />
            <Button
              leftSection={<IconSparkles size={16} />}
              onClick={handleConvert}
              loading={recipeToOrder.isPending}
              disabled={!recipeText.trim()}
            >
              Convert Recipe
            </Button>
          </Stack>
        </Paper>

        {/* Right: Results */}
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Matched Products</Text>
              {result?.products && result.products.length > 0 && (
                <Button
                  leftSection={<IconShoppingCartPlus size={16} />}
                  onClick={handleAddAllToCart}
                  loading={addingToCart}
                  size="sm"
                >
                  Add All to Cart
                </Button>
              )}
            </Group>

            {recipeToOrder.isPending ? (
              <Stack gap="xs">
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </Stack>
            ) : !result ? (
              <EmptyState
                title="No Results Yet"
                description="Paste a recipe on the left and click Convert to find matching products."
                icon={<IconSparkles size={48} />}
              />
            ) : (
              <>
                {result.missing_ingredients &&
                  result.missing_ingredients.length > 0 && (
                    <Alert
                      color="orange"
                      icon={<IconAlertTriangle size={16} />}
                      title="Missing Ingredients"
                    >
                      <Text size="sm">
                        Could not find products for:{' '}
                        {result.missing_ingredients.join(', ')}
                      </Text>
                    </Alert>
                  )}

                {result.products && result.products.length > 0 ? (
                  <>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Product</Table.Th>
                          <Table.Th>Qty</Table.Th>
                          <Table.Th>Price</Table.Th>
                          <Table.Th>Match</Table.Th>
                          <Table.Th>Action</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {result.products.map((item) => (
                          <Table.Tr key={item.product_id}>
                            <Table.Td>
                              <div>
                                <Text fw={500} size="sm">
                                  {item.product_name}
                                </Text>
                                {item.sku && (
                                  <Text size="xs" c="dimmed">
                                    SKU: {item.sku}
                                  </Text>
                                )}
                              </div>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {item.quantity} {item.unit}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">${item.unit_price.toFixed(2)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Progress
                                value={item.confidence * 100}
                                color={confidenceColor(item.confidence)}
                                size="sm"
                                style={{ minWidth: 60 }}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="light"
                                leftSection={<IconShoppingCartPlus size={14} />}
                                onClick={() => handleAddItem(item)}
                                loading={addToCart.isPending}
                              >
                                Add
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                    <Group justify="flex-end">
                      <Text fw={600}>
                        Estimated Total: ${result.total_cost.toFixed(2)}
                      </Text>
                    </Group>
                  </>
                ) : (
                  <EmptyState
                    title="No Products Found"
                    description="We couldn't match any products to the recipe ingredients."
                    icon={<IconSparkles size={48} />}
                  />
                )}
              </>
            )}
          </Stack>
        </Paper>
      </div>
    </Stack>
  );
}
